import { useLocation } from 'react-router-dom'

import { getEngageUrl } from '~publish/components/CommentsSidebar/ChannelNav/helpers'

/**
 * Check if we're currently in the "Comments by Post" view
 * This is determined by the presence of '/by-post/' in the URL path
 */
export const useIsByPostView = (): boolean => {
  const location = useLocation()
  return location.pathname.includes('/by-post/')
}

/**
 * Check if we're currently in the All Channels view (routes under `/community`),
 * as opposed to the single-channel view (`/comments/:channelId`).
 * Used to decide whether a card should badge the author avatar with the
 * source channel's service icon (e.g. collapsed cards in the All Channels view).
 */
export const useIsAllChannelsView = (): boolean => {
  const location = useLocation()
  return location.pathname.startsWith('/community')
}

/**
 * Check if we're on the comments list view (not posts or by-post view)
 * Used to determine if we should use singular path for dialogs
 */
export const useIsCommentsListView = (): boolean => {
  const location = useLocation()
  const pathParts = location.pathname.split('/')
  // We're on list view if path doesn't include 'posts' (plural) or 'by-post'
  const hasPostsViewInPath =
    pathParts.includes('posts') || pathParts.includes('by-post')
  return !hasPostsViewInPath
}

/**
 * Check if the URL is a comment-details deep link (`…/comment/:commentId`,
 * the shared-comment dialog). The dialog overlays whichever Community view
 * is underneath, so background behaviors that navigate — the posts-view
 * preference redirect, the by-post first-post auto-select — must stand down
 * while it's open or they unmount the dialog route.
 */
export const isCommentDetailsPath = (pathname: string): boolean => {
  return pathname.split('/').includes('comment')
}

/**
 * Check if the URL is a mention-details deep link (`…/mention/:mentionId`,
 * the shared-mention dialog). Like the comment dialog, it overlays whichever
 * Community view is underneath, so background behaviors that navigate must
 * stand down while it's open or they unmount the dialog route.
 */
export const isMentionDetailsPath = (pathname: string): boolean => {
  return pathname.split('/').includes('mention')
}

/**
 * Check if the URL is either engagement-details deep link (comment or
 * mention). The shared guard for background navigations that would otherwise
 * unmount an open details dialog.
 */
export const isEngagementDetailsPath = (pathname: string): boolean => {
  return isCommentDetailsPath(pathname) || isMentionDetailsPath(pathname)
}

export const redirectToEngage = (channelId?: string): void => {
  window.location.href = getEngageUrl(channelId)
}
